import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Eye,
  Calendar,
  DollarSign,
  TrendingUp,
  ShoppingBag,
  Clock,
  X,
  RefreshCw,
  Plus,
  Printer,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Badge } from "@/components/admin/ui/Badge";
import { useToast } from "@/components/admin/ui/Toast";

interface Order {
  id: string | number;
  order_number: string;
  student_name: string;
  student_email: string;
  items: string[];
  total: number;
  status: string; // completed, pending, processing, refunded, cancelled
  date: string;
  payment_method: string;
}

export function Orders() {
  const [ordersList, setOrdersList] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { showToast } = useToast();

  // Create Manual Order states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [students, setStudents] = useState<{ id: string | number; name: string; email: string }[]>([]);
  const [courses, setCourses] = useState<{ id: string | number; title: string }[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [customTotal, setCustomTotal] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bKash (বিকাশ)");
  const [orderStatus, setOrderStatus] = useState("completed");
  const [submittingOrder, setSubmittingOrder] = useState(false);

  useEffect(() => {
    // Fetch students list
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (ajaxUrl && (window as any).jQuery) {
      // Get students
      (window as any).jQuery.ajax({
        url: ajaxUrl,
        method: "POST",
        data: {
          action: "chorcha_get_admin_students",
          nonce: adminNonce,
        },
        success: (res: any) => {
          if (res.success && Array.isArray(res.data)) {
            setStudents(res.data);
          }
        }
      });

      // Get courses simple list
      (window as any).jQuery.ajax({
        url: ajaxUrl,
        method: "POST",
        data: {
          action: "chorcha_get_admin_courses_simple",
          nonce: adminNonce,
        },
        success: (res: any) => {
          if (res.success && Array.isArray(res.data)) {
            setCourses(res.data);
          }
        }
      });
    } else {
      // Offline fallback
      setStudents([
        { id: 1, name: "আহমেদ সাজ্জাদ", email: "sajjad@example.com" },
        { id: 2, name: "ইমরান খান", email: "imran@example.com" },
        { id: 3, name: "সাদিয়া সুলতানা", email: "sadia@example.com" }
      ]);
      setCourses([
        { id: "sub_pro_yearly", title: "★ Chorcaghor Pro Subscription (Yearly)" },
        { id: "sub_pro_monthly", title: "★ Chorcaghor Pro Subscription (Monthly)" },
        { id: "sub_max_yearly", title: "★ Chorcaghor Max Subscription (Yearly)" },
        { id: "sub_max_monthly", title: "★ Chorcaghor Max Subscription (Monthly)" },
        { id: 10, title: "এসএসসি ২০২৭ - গণিত" },
        { id: 11, title: "এসএসসি ২০২৭ - পদার্থবিজ্ঞান" },
        { id: 12, title: "এসএসসি ২০২৭ - রসায়ন" },
        { id: 13, title: "এসএসসি ২০২৭ - জীববিজ্ঞান" }
      ]);
    }
  }, []);

  const handleCreateOrder = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudentId) {
      showToast("Please select a student.", "error");
      return;
    }
    if (selectedCourseIds.length === 0) {
      showToast("Please select at least one course.", "error");
      return;
    }

    setSubmittingOrder(true);
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      // Offline mock addition
      const mockStudent = students.find(s => s.id.toString() === selectedStudentId);
      const mockSelectedCourses = courses.filter(c => selectedCourseIds.includes(c.id.toString())).map(c => c.title);
      const newOrder: Order = {
        id: Date.now(),
        order_number: `#SH-${Math.floor(100 + Math.random() * 900)}`,
        student_name: mockStudent?.name || "Offline Student",
        student_email: mockStudent?.email || "offline@example.com",
        items: mockSelectedCourses,
        total: Number(customTotal) || 1500,
        status: orderStatus,
        date: new Date().toISOString().slice(0, 19).replace("T", " "),
        payment_method: paymentMethod,
      };

      setOrdersList(prev => [newOrder, ...prev]);
      setSubmittingOrder(false);
      setIsCreateModalOpen(false);
      setSelectedStudentId("");
      setSelectedCourseIds([]);
      setCustomTotal("");
      showToast("Manual Order successfully created (Offline mode)!", "success");
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_create_admin_order",
        nonce: adminNonce,
        user_id: selectedStudentId,
        course_ids: selectedCourseIds,
        total: customTotal || 0,
        payment_method: paymentMethod,
        status: orderStatus,
      },
      success: (res: any) => {
        setSubmittingOrder(false);
        if (res.success) {
          showToast(res.data?.message || "Manual Order successfully created!", "success");
          setIsCreateModalOpen(false);
          setSelectedStudentId("");
          setSelectedCourseIds([]);
          setCustomTotal("");
          fetchOrders(); // Refresh order listing
        } else {
          showToast(res.data || "Failed to create manual order.", "error");
        }
      },
      error: () => {
        setSubmittingOrder(false);
        showToast("Error executing manual order creation.", "error");
      }
    });
  };

  const handlePrintInvoice = (order: Order) => {
    const printWindow = window.open("", "_blank", "width=800,height=900");
    if (!printWindow) {
      showToast("Pop-up blocker is preventing printing.", "error");
      return;
    }

    const itemsHtml = order.items
      .map(
        (item) => `
        <div style="padding: 12px; border: 1px solid #E5E7EB; border-radius: 12px; font-weight: bold; color: #374151; display: flex; justify-content: space-between; margin-bottom: 8px;">
          <span>${item}</span>
          <span style="color: #9CA3AF; font-weight: 500;">Qty: 1</span>
        </div>
      `
      )
      .join("");

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${order.order_number}</title>
        <meta charset="utf-8">
        <style>
          body {
            font-family: 'Hind Siliguri', 'Inter', -apple-system, sans-serif;
            background-color: #ffffff;
            color: #1F2937;
            padding: 40px;
          }
          .invoice-box {
            max-width: 600px;
            margin: auto;
            border: 1px solid #F3F4F6;
            padding: 30px;
            border-radius: 24px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid #F3F4F6;
            padding-bottom: 20px;
            margin-bottom: 20px;
          }
          .logo {
            font-size: 24px;
            font-weight: 800;
            color: #00C9A7;
          }
          .meta-info {
            background-color: #F9FAFB;
            padding: 16px;
            border-radius: 16px;
            margin-bottom: 20px;
          }
          .meta-row {
            display: flex;
            justify-content: space-between;
            font-size: 13px;
            margin-bottom: 8px;
          }
          .meta-row:last-child {
            margin-bottom: 0;
          }
          .label {
            color: #9CA3AF;
            font-weight: 500;
          }
          .value {
            font-weight: 700;
            color: #1F2937;
          }
          .section-title {
            font-size: 11px;
            font-weight: 700;
            color: #9CA3AF;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-top: 20px;
            margin-bottom: 10px;
          }
          .student-box {
            border: 1px solid #F3F4F6;
            padding: 12px;
            border-radius: 12px;
            font-size: 13px;
            margin-bottom: 20px;
          }
          .total-box {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-top: 1px dashed #E5E7EB;
            padding-top: 15px;
            margin-top: 20px;
          }
          .total-title {
            font-size: 14px;
            font-weight: 700;
          }
          .total-amount {
            font-size: 20px;
            font-weight: 800;
            color: #00C9A7;
          }
          @media print {
            body { padding: 0; }
            .invoice-box { border: none; box-shadow: none; padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="invoice-box">
          <div class="header">
            <div>
              <div class="logo">শুদ্ধশিক্ষা</div>
              <div style="font-size: 11px; color: #9CA3AF; margin-top: 4px;">শিখুন শেষ রক্তবিন্দু পর্যন্ত</div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 18px; font-weight: 800; color: #374151;">রশিদ / Invoice</div>
              <div style="font-size: 11px; color: #9CA3AF; margin-top: 4px;">রশিদ নং: ${order.order_number}</div>
            </div>
          </div>

          <div class="meta-info">
            <div class="meta-row">
              <span class="label">অর্ডার নম্বর (Order Number):</span>
              <span class="value">${order.order_number}</span>
            </div>
            <div class="meta-row">
              <span class="label">তারিখ (Date):</span>
              <span class="value">${order.date}</span>
            </div>
            <div class="meta-row">
              <span class="label">পেমেন্ট মাধ্যম (Payment Method):</span>
              <span class="value">${order.payment_method}</span>
            </div>
            <div class="meta-row">
              <span class="label">স্ট্যাটাস (Status):</span>
              <span class="value" style="color: ${order.status === 'completed' ? '#10B981' : '#F59E0B'}">${order.status.toUpperCase()}</span>
            </div>
          </div>

          <div>
            <div class="section-title">শিক্ষার্থীর বিবরণ (Student Details)</div>
            <div class="student-box">
              <div style="font-weight: bold; margin-bottom: 4px;">${order.student_name}</div>
              <div style="color: #6B7280;">${order.student_email}</div>
            </div>
          </div>

          <div>
            <div class="section-title">ক্রয়কৃত курсসমূহ (Courses Purchased)</div>
            ${itemsHtml}
          </div>

          <div class="total-box">
            <span class="total-title">পরিশোধিত মূল্য (Paid Amount)</span>
            <span class="total-amount">৳${order.total.toLocaleString()}</span>
          </div>

          <div style="margin-top: 40px; text-align: center; font-size: 11px; color: #9CA3AF; border-top: 1px solid #F3F4F6; padding-top: 15px;">
            এটি একটি সিস্টেম জেনারেটেড রশিদ। কোনো স্বাক্ষরের প্রয়োজন নেই।
          </div>
        </div>
        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  function fetchOrders() {
    setLoading(true);
    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      // Offline fallback for development/sandbox
      setOrdersList([
        {
          id: 101,
          order_number: "#SH-101",
          student_name: "আহমেদ সাজ্জাদ",
          student_email: "sajjad@example.com",
          items: ["এসএসসি ২০২৭ - গণিত", "এসএসসি ২০২৭ - পদার্থবিজ্ঞান"],
          total: 3000,
          status: "completed",
          date: "2026-06-28 10:30:15",
          payment_method: "bKash (বিকাশ)",
        },
        {
          id: 102,
          order_number: "#SH-102",
          student_name: "ইমরান খান",
          student_email: "imran@example.com",
          items: ["এসএসসি ২০২৭ - রসায়ন"],
          total: 1500,
          status: "pending",
          date: "2026-06-28 12:15:22",
          payment_method: "Nagad (নগদ)",
        },
        {
          id: 103,
          order_number: "#SH-103",
          student_name: "সাদিয়া সুলতানা",
          student_email: "sadia@example.com",
          items: ["এসএসসি ২০২৭ - জীববিজ্ঞান"],
          total: 1500,
          status: "cancelled",
          date: "2026-06-27 15:45:00",
          payment_method: "Rocket (রকেট)",
        }
      ]);
      setLoading(false);
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_get_admin_orders",
        nonce: adminNonce,
      },
      success: (res: any) => {
        setLoading(false);
        if (res.success && Array.isArray(res.data)) {
          setOrdersList(res.data);
        } else {
          showToast("Failed to parse orders data.", "error");
        }
      },
      error: (err: any) => {
        setLoading(false);
        showToast("Error loading orders list.", "error");
        console.error("AJAX Error:", err);
      },
    });
  };

  const handleConfirmOrder = (orderId: string | number) => {
    if (!confirm(`Are you sure you want to manually confirm and complete Order #${orderId}?`)) {
      return;
    }

    const ajaxUrl =
      (window as any).shLmsAdmin?.ajax_url ||
      (window as any).hubInstructor?.ajax_url ||
      (window as any).ajaxurl;
    const adminNonce =
      (window as any).shLmsAdmin?.nonce ||
      (window as any).chorchaAdmin?.nonce ||
      (window as any).ajaxurl;

    if (!ajaxUrl || !(window as any).jQuery) {
      // Offline fallback: update local state
      setOrdersList((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: "completed" } : o))
      );
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder((prev) => prev ? { ...prev, status: "completed" } : null);
      }
      showToast("Order manually confirmed (offline)!", "success");
      return;
    }

    (window as any).jQuery.ajax({
      url: ajaxUrl,
      method: "POST",
      data: {
        action: "chorcha_confirm_admin_order",
        nonce: adminNonce,
        id: orderId,
      },
      success: (res: any) => {
        if (res.success) {
          showToast(res.data.message || "Order confirmed successfully!", "success");
          fetchOrders();
          setSelectedOrder(null);
        } else {
          showToast(res.data || "Failed to confirm order.", "error");
        }
      },
      error: () => {
        showToast("Error sending request.", "error");
      },
    });
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
      case "success":
        return <Badge variant="green">Completed</Badge>;
      case "pending":
      case "processing":
        return <Badge variant="yellow">Pending</Badge>;
      case "cancelled":
      case "failed":
        return <Badge variant="red">Cancelled</Badge>;
      default:
        return <Badge variant="gray">{status}</Badge>;
    }
  };

  const filteredOrders = ordersList.filter((order) => {
    const query = search.toLowerCase();
    const matchesSearch =
      order.student_name.toLowerCase().includes(query) ||
      order.student_email.toLowerCase().includes(query) ||
      order.order_number.toLowerCase().includes(query) ||
      order.items.some((item) => item.toLowerCase().includes(query));

    const matchesStatus =
      statusFilter === "all" || order.status.toLowerCase() === statusFilter.toLowerCase();

    const matchesDate =
      !dateFilter || order.date.split(" ")[0] === dateFilter;

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Analytics helper calculations
  const totalEarnings = ordersList
    .filter((o) => o.status === "completed")
    .reduce((sum, o) => sum + o.total, 0);

  const pendingEarnings = ordersList
    .filter((o) => o.status === "pending" || o.status === "processing")
    .reduce((sum, o) => sum + o.total, 0);

  const totalOrders = ordersList.length;
  const completedOrders = ordersList.filter((o) => o.status === "completed").length;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
          <p className="text-sm text-gray-500 mt-1">
            Track transactions, student course purchases, and enrollments
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-center">
          <Button
            variant="outline"
            leftIcon={<RefreshCw className="h-4 w-4" />}
            onClick={fetchOrders}
          >
            Refresh List
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00C9A7] hover:bg-[#00b898] text-white"
          >
            Add New Order
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Total Revenue</span>
            <span className="text-xl font-extrabold text-gray-800">৳{totalEarnings.toLocaleString()}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-50 rounded-xl text-amber-600">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Pending Amount</span>
            <span className="text-xl font-extrabold text-gray-800">৳{pendingEarnings.toLocaleString()}</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
            <ShoppingBag className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Total Sales</span>
            <span className="text-xl font-extrabold text-gray-800">{totalOrders} Orders</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border border-gray-150 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider block">Conversion Rate</span>
            <span className="text-xl font-extrabold text-gray-800">
              {totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0}%
            </span>
          </div>
        </div>
      </div>

      {/* Filters and List */}
      <div className="bg-white rounded-3xl border border-gray-150 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-4.5 w-4.5" />
            <Input
              type="text"
              placeholder="Search by student, course, email or order ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 text-sm border-gray-200"
            />
          </div>

          <div className="flex items-center gap-2 self-end">
            <Button
              variant={filtersOpen ? "primary" : "outline"}
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={() => setFiltersOpen(!filtersOpen)}
              className="text-xs"
            >
              Filter Options
            </Button>
          </div>
        </div>

        {/* Expandable Filter Box */}
        {filtersOpen && (
          <div className="p-5 bg-gray-50 border-b border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1 text-left">
              <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">Payment Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold focus:outline-none focus:border-[#00C9A7]"
              >
                <option value="all">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="space-y-1 text-left">
              <label className="text-[10px] font-bold uppercase text-gray-500 tracking-wider">Order Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <Input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="pl-9 text-xs border-gray-200 py-2 h-auto"
                />
              </div>
            </div>

            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setStatusFilter("all");
                  setDateFilter("");
                  setFiltersOpen(false);
                }}
                className="w-full justify-center text-xs text-gray-500 border-gray-200"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        )}

        {/* Orders Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-4 border-[#00C9A7]/20 border-t-[#00C9A7] rounded-full animate-spin" />
              <span className="text-xs text-gray-400 font-semibold font-bangla">অর্ডার তালিকা লোড হচ্ছে...</span>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-20 space-y-3">
              <div className="text-4xl">🛒</div>
              <h3 className="font-bangla text-base font-bold text-gray-700">কোনো অর্ডার পাওয়া যায়নি!</h3>
              <p className="text-xs text-gray-400">ফিল্টার বা সার্চ কিউরি পরিবর্তন করে আবার চেষ্টা করুন</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider pl-6">Order ID</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Student</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Courses</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Total</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Method</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Status</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider">Date</th>
                  <th className="p-4 text-[10px] font-extrabold uppercase text-gray-400 tracking-wider text-right pr-6">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4 font-semibold text-gray-700 text-xs pl-6">{order.order_number}</td>
                    <td className="p-4">
                      <div className="flex flex-col text-left">
                        <span className="font-bold text-gray-800 text-sm leading-tight">{order.student_name}</span>
                        <span className="text-[10px] text-gray-400">{order.student_email}</span>
                      </div>
                    </td>
                    <td className="p-4 max-w-[240px]">
                      <div className="flex flex-wrap gap-1">
                        {order.items.map((item, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-semibold truncate max-w-[180px]" title={item}>
                            {item}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-4 font-extrabold text-gray-900 text-sm">৳{order.total.toLocaleString()}</td>
                    <td className="p-4 text-xs font-semibold text-gray-500">{order.payment_method}</td>
                    <td className="p-4">{getStatusBadge(order.status)}</td>
                    <td className="p-4 text-[10px] text-gray-400 font-semibold">{order.date}</td>
                    <td className="p-4 text-right pr-6 space-x-1.5">
                      {["pending", "processing", "on-hold", "onhold"].includes(order.status.toLowerCase()) && (
                        <button
                          onClick={() => handleConfirmOrder(order.id)}
                          className="p-1.5 hover:bg-emerald-50 text-emerald-500 hover:text-emerald-700 rounded-lg cursor-pointer transition-colors inline-flex items-center"
                          title="Confirm & Complete Order"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="p-1.5 hover:bg-gray-150 text-gray-400 hover:text-gray-700 rounded-lg cursor-pointer transition-colors inline-flex items-center"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Details View Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 relative shadow-2xl border border-gray-150 space-y-5 text-left">
            <button
              onClick={() => setSelectedOrder(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <h3 className="text-lg font-bold text-gray-800">Order Invoice Detail</h3>
              <p className="text-xs text-gray-400 mt-1">Transaction information overview</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 font-medium">Order Number:</span>
                <span className="font-bold text-gray-800">{selectedOrder.order_number}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 font-medium">Transaction Date:</span>
                <span className="font-bold text-gray-800">{selectedOrder.date}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 font-medium">Payment Gateway:</span>
                <span className="font-bold text-gray-800">{selectedOrder.payment_method}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-400 font-medium">Status:</span>
                <div>{getStatusBadge(selectedOrder.status)}</div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Student Details</h4>
              <div className="border border-gray-100 rounded-xl p-3 flex flex-col gap-1 text-xs">
                <span className="font-bold text-gray-800">{selectedOrder.student_name}</span>
                <span className="text-gray-400">{selectedOrder.student_email}</span>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Items Ordered</h4>
              <div className="space-y-1">
                {selectedOrder.items.map((item, idx) => (
                  <div key={idx} className="p-3 bg-white border border-gray-100 rounded-xl text-xs font-bold text-gray-700 flex justify-between">
                    <span>{item}</span>
                    <span className="text-gray-400 font-medium">Qty: 1</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
              <span className="text-sm font-bold text-gray-800">Paid Amount</span>
              <span className="text-xl font-extrabold text-[#00C9A7]">৳{selectedOrder.total.toLocaleString()}</span>
            </div>

            <div className="flex gap-3 pt-2">
              {["pending", "processing", "on-hold", "onhold"].includes(selectedOrder.status.toLowerCase()) && (
                <Button
                  onClick={() => handleConfirmOrder(selectedOrder.id)}
                  className="flex-1 justify-center bg-[#00C9A7] hover:bg-[#00b898] text-white"
                >
                  Confirm & Approve
                </Button>
              )}
              <Button
                variant="outline"
                leftIcon={<Printer className="w-4 h-4" />}
                onClick={() => handlePrintInvoice(selectedOrder)}
                className="justify-center border-gray-200 hover:bg-gray-50 text-gray-700"
              >
                Print Invoice
              </Button>
              <Button
                variant="outline"
                className="justify-center"
                onClick={() => setSelectedOrder(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Manual/Offline Order Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 relative shadow-2xl border border-gray-150 space-y-5 text-left max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div>
              <h3 className="text-lg font-bold text-gray-800">Add New Order</h3>
              <p className="text-xs text-gray-400 mt-1">Enroll a student in course(s) and record an offline/manual payment.</p>
            </div>

            <form onSubmit={handleCreateOrder} className="space-y-4">
              {/* Select Student */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Select Student</label>
                <select
                  value={selectedStudentId}
                  onChange={(e) => setSelectedStudentId(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00C9A7]/20 focus:border-[#00C9A7] bg-white"
                  required
                >
                  <option value="">-- Choose Student --</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.name} ({student.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Courses */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Select Courses</label>
                <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-2 bg-white">
                  {courses.map((course) => {
                    const isChecked = selectedCourseIds.includes(course.id.toString());
                    return (
                      <label key={course.id} className="flex items-center gap-2.5 text-xs text-gray-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              setSelectedCourseIds(prev => prev.filter(id => id !== course.id.toString()));
                            } else {
                              setSelectedCourseIds(prev => [...prev, course.id.toString()]);
                            }
                          }}
                          className="rounded text-[#00C9A7] focus:ring-[#00C9A7] cursor-pointer"
                        />
                        <span className="font-medium">{course.title}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Total Paid Amount */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Total Amount (৳)</label>
                <Input
                  type="number"
                  placeholder="e.g. 1500"
                  value={customTotal}
                  onChange={(e) => setCustomTotal(e.target.value)}
                  className="text-sm border-gray-200"
                  required
                />
              </div>

              {/* Payment Method */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Payment Gateway (Offline/Manual)</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00C9A7]/20 focus:border-[#00C9A7] bg-white"
                >
                  <option value="bKash (বিকাশ)">bKash (বিকাশ)</option>
                  <option value="Nagad (নগদ)">Nagad (নগদ)</option>
                  <option value="Rocket (রকেট)">Rocket (রকেট)</option>
                  <option value="Bank Transfer">Bank Transfer (ব্যাংক ট্রান্সফার)</option>
                  <option value="Cash Payment">Cash Payment (নগদ টাকা)</option>
                </select>
              </div>

              {/* Order Status */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Initial Status</label>
                <select
                  value={orderStatus}
                  onChange={(e) => setOrderStatus(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#00C9A7]/20 focus:border-[#00C9A7] bg-white"
                >
                  <option value="completed">Completed (Enrolls student instantly)</option>
                  <option value="pending">Pending (Awaiting payment approval)</option>
                  <option value="processing">Processing</option>
                </select>
              </div>

              {/* Form buttons */}
              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <Button
                  type="submit"
                  disabled={submittingOrder}
                  className="flex-1 justify-center bg-[#00C9A7] hover:bg-[#00b898] text-white"
                >
                  {submittingOrder ? "Creating..." : "Create Order"}
                </Button>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="flex-1 justify-center"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
